import React, { useState } from 'react';
import { createBookingReview } from '../../api/reviews';
import { uploadOfferingImage } from '../../api/offerings';
import './LeaveReviewModal.css';

export default function LeaveReviewModal({ isOpen, onClose, bookingId, onSubmitSuccess }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [photoUrls, setPhotoUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleRatingClick = (val) => setRating(val);
  const handleRatingHover = (val) => setHoverRating(val);
  const handleRatingLeave = () => setHoverRating(0);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setError('');
    
    for (const file of files) {
      try {
        const res = await uploadOfferingImage(file);
        if (res?.url) {
          setPhotoUrls((prev) => [...prev, res.url]);
        }
      } catch (err) {
        console.error("Evidence upload failed:", err);
        setError("Failed to upload image. Please try again.");
      }
    }
    setUploading(false);
  };

  const removePhoto = (index) => {
    setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError("Please select a star rating.");
      return;
    }
    if (comment.trim().length < 20) {
      setError(`Written review must be at least 20 characters (currently ${comment.trim().length}).`);
      return;
    }
    if (comment.trim().length > 1000) {
      setError("Written review cannot exceed 1000 characters.");
      return;
    }

    setSubmitting(true);
    try {
      await createBookingReview(bookingId, { rating, comment, photoUrls });
      onSubmitSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="review-modal-header">
          <h3>Leave a <span className="text-gradient">Review</span></h3>
          <button className="review-close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="review-form">
          <div className="review-form-body">
            {error && <div className="review-error-box">{error}</div>}

            {/* Star Selection */}
            <div className="review-form-group rating-group">
              <label>Overall Rating</label>
              <div className="star-rating-container" onMouseLeave={handleRatingLeave}>
                {[1, 2, 3, 4, 5].map((star) => {
                  const active = hoverRating ? star <= hoverRating : star <= rating;
                  return (
                    <span
                      key={star}
                      className={`star-icon ${active ? 'active' : ''}`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => handleRatingHover(star)}
                    >
                      ★
                    </span>
                  );
                })}
              </div>
              {rating > 0 && <span className="rating-label">{rating} / 5 Stars</span>}
            </div>

            {/* Written Comment */}
            <div className="review-form-group">
              <div className="label-row">
                <label htmlFor="comment">Written Review</label>
                <span className={`char-count ${comment.length < 20 ? 'insufficient' : ''}`}>
                  {comment.length} / 1000 (Min 20)
                </span>
              </div>
              <textarea
                id="comment"
                rows="5"
                className="review-textarea"
                placeholder="Tell us about your experience with this service provider (min 20 characters)..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            {/* Photo Attachments */}
            <div className="review-form-group">
              <label>Photos (Optional)</label>
              <div className="photo-upload-container">
                <label className="photo-upload-btn">
                  <span>📷 Upload Photos</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading || submitting}
                    style={{ display: 'none' }}
                  />
                </label>
                {uploading && <span className="upload-loader">Uploading...</span>}
              </div>

              {photoUrls.length > 0 && (
                <div className="review-photo-preview-grid">
                  {photoUrls.map((url, index) => (
                    <div key={index} className="review-photo-item">
                      <img src={url} alt={`Preview ${index}`} />
                      <button type="button" className="remove-photo-btn" onClick={() => removePhoto(index)}>
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="review-modal-actions">
            <button type="button" className="review-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="review-btn-primary" disabled={submitting || uploading}>
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
